/* eslint-disable no-param-reassign */
import { FC } from 'react';
import styled from '@emotion/styled';
import styleHelper from '@utils/styleHelper';
import { subscriptionTypes } from '@constants/index';
import { Spacings, mediaQuery } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import { ISubscriptionInfoProps } from 'types/checkout';
import { emptyObject } from '@constants/empty';
import camelcaseKeys from 'camelcase-keys';
import usePaymentMethods from '@hooks/usePaymentMethods';
import SubscriptionAmountInfo from './SubscriptionAmountInfo';
import SubscriptionInfoOffer from './SubscriptionInfoOffer';
import ReferralSection from './ReferralSection';
import DurationInfo from './DurationInfo';
import { getTotalAmtBySubscription } from './usePurchaseDetails';

const Wrapper = styled.div<{
  insideCurtain: boolean;
  $mwebCheckoutV2: boolean;
}>`
  position: ${({ insideCurtain }) => (insideCurtain ? 'relative' : 'fixed')};
  width: ${({ insideCurtain }) =>
    insideCurtain ? 'auto' : styleHelper.getWidthFromGridColumn(8)};
  /* height: calc(100vh - 124px); */
  margin-bottom: ${Spacings.SPACING_3B};
  overflow-y: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  /* Chrome and IOS Safari */
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${({ $mwebCheckoutV2 }) =>
      $mwebCheckoutV2 ? '0px' : '12px'};
  }
`;

const SubscriptionInfo: FC<ISubscriptionInfoProps> = ({
  isReferralPreapplied = false,
  changeDuration,
  onApply,
  resetReferral,
  hideCredits = false,
  insideCurtain = false,
  referralInputRef = null,
  applyCredits = () => {},
  removeCredits = () => {},
  isCombo = false,
  subscription,
  discountedSubscription,
  referral,
  creditsApplied = false,
  partPaymentActive = false,
  partPaymentStatus = null,
  cashResponse = null,
  setLoanInterestAmt = () => {},
  isLoanSelected = false,
  loanMeta = emptyObject,
  bundleDetails = null,
  goal,
  extraDuration = 0,
  referralError,
  me,
  isSuggestionFlow = false,
  validateReferralInProgress = false,
  isMandate
}) => {
  const { state: { mwebCheckoutV2 } = {} } = usePaymentMethods();
  // This component is used in multiple places, we will remove this once we update in other places too
  subscription = camelcaseKeys(subscription, { deep: true });
  discountedSubscription = camelcaseKeys(discountedSubscription, {
    deep: true
  });

  const isMobile = useIsMobile();

  const isUaIconGoal = goal?.isUaIconGoal || false;

  const referralSubmitted = !!referral;
  const isReferralValid = discountedSubscription?.referral?.referralType;
  const subscriptionData = discountedSubscription?.subscription || subscription;

  let referralDiscount = 0;
  if (discountedSubscription?.subscription && creditsApplied) {
    referralDiscount =
      getTotalAmtBySubscription(subscription, true) -
      getTotalAmtBySubscription(discountedSubscription?.subscription, true);
  } else if (discountedSubscription?.subscription && !creditsApplied) {
    referralDiscount =
      getTotalAmtBySubscription(subscription) -
      getTotalAmtBySubscription(discountedSubscription?.subscription);
  }

  const credits =
    subscriptionData?.redeemableCreditAmount ||
    subscription?.redeemableCreditAmount ||
    0;

  const isPlatform = subscription?.type === subscriptionTypes.PLATFORM;
  const hideReferralBlock = !me?.uid && isPlatform;

  return (
    <Wrapper insideCurtain $mwebCheckoutV2={mwebCheckoutV2}>
      {isMobile &&
      mwebCheckoutV2 &&
      (partPaymentActive || cashResponse) ? null : (
        <DurationInfo
          changeDuration={changeDuration}
          isCombo={isCombo}
          insideCurtain={insideCurtain}
          subscription={subscription}
          partPaymentStatus={partPaymentStatus}
          cashResponse={cashResponse}
          bundleDetails={bundleDetails}
          goal={goal}
          extraDuration={extraDuration}
          creditsApplied={creditsApplied}
          discountedReferralSubscription={
            referralSubmitted && isReferralValid
              ? discountedSubscription?.subscription
              : subscription
          }
          credits={credits}
          isReferralValid={isReferralValid}
          referralCode={referral}
          setLoanInterestAmt={setLoanInterestAmt}
          isLoanSelected={isLoanSelected}
          loanMeta={loanMeta}
          referralDiscount={referralDiscount}
          mwebCheckoutV2={mwebCheckoutV2}
          isMandate={isMandate}
        />
      )}

      {!isMobile && <SubscriptionInfoOffer extraDuration={extraDuration} />}
      {!partPaymentActive &&
        !cashResponse &&
        !isUaIconGoal &&
        !hideReferralBlock && (
          <ReferralSection
            isReferralPreapplied={isReferralPreapplied}
            onApply={onApply}
            resetReferral={resetReferral}
            referralInputRef={referralInputRef}
            removeCredits={removeCredits}
            applyCredits={applyCredits}
            isCombo={isCombo}
            subscription={subscription}
            discountedSubscription={discountedSubscription}
            referral={referral}
            credits={credits}
            creditsApplied={creditsApplied}
            goal={goal}
            referralError={referralError}
            referralSubmitted={referralSubmitted}
            isReferralValid={isReferralValid}
            hideCredits={hideCredits}
            isSuggestionFlow={isSuggestionFlow}
            validateReferralInProgress={validateReferralInProgress}
            mwebCheckoutV2={mwebCheckoutV2}
          />
        )}
      {!mwebCheckoutV2 && isMobile && (
        <SubscriptionInfoOffer extraDuration={extraDuration} />
      )}
      {!(mwebCheckoutV2 && isMobile) && (
        <SubscriptionAmountInfo
          subscription={
            referralSubmitted && isReferralValid
              ? discountedSubscription?.subscription
              : subscription
          }
          creditsApplied={creditsApplied}
          credits={credits}
          isReferralValid={isReferralValid}
          referralDiscount={referralDiscount}
          referralCode={referral}
          partPaymentStatus={partPaymentStatus}
          cashResponse={cashResponse}
          setLoanInterestAmt={setLoanInterestAmt}
          isLoanSelected={isLoanSelected}
          loanMeta={loanMeta}
          mwebCheckoutV2={mwebCheckoutV2}
        />
      )}
    </Wrapper>
  );
};

export default SubscriptionInfo;
